import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, PencilSimple, Star, ArrowsOutSimple, DownloadSimple, Trash, CaretDown, CaretUp, UploadSimple, Sparkle, FolderSimple, Spinner } from "@phosphor-icons/react";
import { FreigabeSwitch } from "@/components/PortalPanel";
import AnnotViewer from "./AnnotViewer";
import { KAT_LABEL, STATUS, statusOf, fmtSize, fmtDate, DocIcon } from "./dmsUtils";

export default function DmsPreview({ doc, akten, allTags, onPatch, onDelete, onClose, onFull, onAnnotCount, onReplace }) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(doc.original_filename);
  const [details, setDetails] = useState(true);
  const [notiz, setNotiz] = useState(doc.notiz || "");
  const [tagInput, setTagInput] = useState("");
  const [versionBusy, setVersionBusy] = useState(false);
  const versionRef = useRef();

  useEffect(() => { setName(doc.original_filename); setNotiz(doc.notiz || ""); setRenaming(false); }, [doc.id, doc.original_filename, doc.notiz]);

  const patch = async (p, msg) => {
    try {
      await api.patch(`/dokumente/${doc.id}`, p);
      onPatch(doc.id, p);
      if (msg) toast.success(msg);
    } catch (e) { toast.error(errMsg(e, "Änderung fehlgeschlagen")); }
  };

  const rename = () => {
    const n = name.trim();
    if (!n || n === doc.original_filename) { setRenaming(false); setName(doc.original_filename); return; }
    patch({ original_filename: n }, "Umbenannt");
    setRenaming(false);
  };

  const addTag = (t) => {
    const tag = (t || tagInput).trim().replace(/^#/, "").slice(0, 40);
    if (!tag) return;
    if (!(doc.tags || []).includes(tag)) patch({ tags: [...(doc.tags || []), tag] });
    setTagInput("");
  };
  const removeTag = (tag) => patch({ tags: (doc.tags || []).filter(x => x !== tag) });

  const download = async () => {
    try {
      const r = await api.get(`/dokumente/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.original_filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(errMsg(e, "Download fehlgeschlagen")); }
  };

  const downloadVersion = async (v) => {
    try {
      const r = await api.get(`/dokumente/${doc.id}/versionen/${v.nr}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = v.original_filename || `version-${v.nr}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(errMsg(e, "Download fehlgeschlagen")); }
  };

  const uploadVersion = async (file) => {
    if (!file) return;
    setVersionBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/dokumente/${doc.id}/version`, fd);
      onReplace(r.data);
      toast.success(`Version ${r.data.version_nr || ""} hochgeladen`);
    } catch (e) { toast.error(errMsg(e, "Version konnte nicht hochgeladen werden")); }
    finally { setVersionBusy(false); }
  };

  const suggestions = allTags.filter(t => !(doc.tags || []).includes(t.tag) && (!tagInput || t.tag.toLowerCase().includes(tagInput.toLowerCase()))).slice(0, 6);

  return (
    <div className="h-full flex flex-col min-h-0 bg-white" data-testid="dms-preview">
      <div className="px-3 py-2.5 border-b border-[#E5E7EB] flex items-center gap-2">
        <DocIcon d={doc} size={24} />
        {renaming ? (
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onBlur={rename} onKeyDown={(e) => { if (e.key === "Enter") rename(); if (e.key === "Escape") { setRenaming(false); setName(doc.original_filename); } }} className="h-8 text-sm flex-1" data-testid="dms-rename-input" />
        ) : (
          <button type="button" onClick={() => setRenaming(true)} className="flex-1 min-w-0 text-left group flex items-center gap-1.5" title="Umbenennen" data-testid="dms-rename-btn">
            <span className="text-sm font-medium text-[#0A192F] truncate" data-testid="dms-preview-name">{doc.original_filename}</span>
            <PencilSimple size={13} className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 shrink-0" />
          </button>
        )}
        <button type="button" onClick={() => patch({ favorit: !doc.favorit })} className={`h-8 w-8 rounded-md flex items-center justify-center ${doc.favorit ? "text-[#F59E0B]" : "text-[#9CA3AF] hover:text-[#F59E0B]"}`} title="Favorit" data-testid="dms-preview-star"><Star size={17} weight={doc.favorit ? "fill" : "regular"} /></button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Vollbild" onClick={onFull} data-testid="dms-preview-full"><ArrowsOutSimple size={16} /></Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Herunterladen" onClick={download} data-testid="dms-preview-download"><DownloadSimple size={16} /></Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#D32F2F]" title="Löschen" onClick={() => onDelete(doc)} data-testid="dms-preview-delete"><Trash size={16} /></Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Schließen" onClick={onClose} data-testid="dms-preview-close"><X size={16} /></Button>
      </div>

      <button type="button" onClick={() => setDetails(v => !v)} className="px-3 h-8 flex items-center justify-between text-[10px] uppercase tracking-wider text-[#9CA3AF] hover:text-[#0A192F] border-b border-[#F3F4F6]" data-testid="dms-details-toggle">
        <span>Details {details ? "ausblenden" : "einblenden"}</span>{details ? <CaretUp size={12} /> : <CaretDown size={12} />}
      </button>

      {details && (
        <div className="px-3 py-3 border-b border-[#E5E7EB] space-y-3 text-xs overflow-y-auto max-h-[45%]" data-testid="dms-details">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Akte</p>
              <Select value={doc.akte_id || "none"} onValueChange={(v) => patch({ akte_id: v === "none" ? null : v }, v === "none" ? "Zuordnung entfernt" : "Akte zugeordnet")}>
                <SelectTrigger className="h-8 text-xs" data-testid="dms-meta-akte"><SelectValue placeholder="Ohne Akte" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ohne Akte</SelectItem>
                  {akten.map(a => <SelectItem key={a.id} value={a.id}>{a.aktenzeichen} · {a.mandant_name || "—"}</SelectItem>)}
                </SelectContent>
              </Select>
              {doc.akte && <Link to={`/app/akten/${doc.akte.id}`} className="inline-flex items-center gap-1 mt-1 text-[11px] text-[#1A2238] hover:underline" data-testid="dms-meta-akte-link"><FolderSimple size={12} /> Akte öffnen</Link>}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Kategorie</p>
              <Select value={doc.kategorie} onValueChange={(v) => patch({ kategorie: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="dms-meta-kategorie"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(KAT_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Status</p>
              <div className="inline-flex rounded-md border border-[#E5E7EB] overflow-hidden" data-testid="dms-meta-status">
                {STATUS.map(s => (
                  <button key={s.key} type="button" onClick={() => patch({ dms_status: s.key })} data-testid={`dms-status-${s.key}`}
                    className={`h-7 px-2.5 text-[11px] font-medium flex items-center gap-1.5 transition-colors ${statusOf(doc) === s.key ? "bg-[#1A2238] text-white" : "bg-white text-[#4B5563] hover:bg-[#F9FAFB]"}`}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.dot }} />{s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Mandantenportal</p>
              {doc.akte_id ? <FreigabeSwitch doc={doc} onChanged={() => onPatch(doc.id, { mandant_sichtbar: !doc.mandant_sichtbar })} /> : <span className="text-[11px] text-[#9CA3AF]">Erst Akte zuordnen</span>}
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Schlagwörter</p>
            <div className="flex flex-wrap items-center gap-1.5" data-testid="dms-tags">
              {(doc.tags || []).map(t => (
                <span key={t} className="inline-flex items-center gap-1 bg-[#1A2238]/5 text-[#1A2238] rounded px-1.5 py-0.5 text-[11px]" data-testid={`dms-tag-${t}`}>#{t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-[#D32F2F]" title="Entfernen" data-testid={`dms-tag-remove-${t}`}><X size={10} /></button>
                </span>
              ))}
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }} onBlur={() => tagInput && addTag()}
                placeholder="+ Schlagwort" className="h-6 text-[11px] px-1.5 rounded border border-dashed border-[#D1D5DB] bg-transparent w-28 focus:outline-none focus:border-[#1A2238]" data-testid="dms-tag-input" />
            </div>
            {tagInput && suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">{suggestions.map(t => <button key={t.tag} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => addTag(t.tag)} className="text-[10px] text-[#4B5563] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded px-1.5 py-0.5">#{t.tag}</button>)}</div>
            )}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Notiz</p>
            <Textarea rows={2} value={notiz} onChange={(e) => setNotiz(e.target.value)} onBlur={() => { if ((doc.notiz || "") !== notiz) patch({ notiz }); }} placeholder="Interne Notiz zum Dokument …" className="text-xs min-h-0" data-testid="dms-notiz" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">Versionen ({(doc.versionen?.length || 0) + 1})</p>
              <input ref={versionRef} type="file" accept="application/pdf,image/*" className="hidden" data-testid="dms-version-input" onChange={(e) => { uploadVersion(e.target.files?.[0]); e.target.value = ""; }} />
              <button type="button" onClick={() => versionRef.current?.click()} disabled={versionBusy} className="text-[11px] text-[#1A2238] hover:underline inline-flex items-center gap-1" data-testid="dms-version-upload">
                {versionBusy ? <Spinner size={12} className="animate-spin" /> : <UploadSimple size={12} />} Neue Version
              </button>
            </div>
            <ul className="text-[11px] text-[#4B5563] space-y-0.5" data-testid="dms-versionen">
              <li className="flex justify-between"><span className="font-medium text-[#0A192F]">v{doc.version_nr || 1} (aktuell)</span><span className="font-mono">{fmtDate(doc.version_datum || doc.created_at)} · {fmtSize(doc.size)}</span></li>
              {[...(doc.versionen || [])].reverse().map(v => (
                <li key={v.nr} className="flex justify-between">
                  <button type="button" onClick={() => downloadVersion(v)} className="hover:underline" data-testid={`dms-version-${v.nr}`}>v{v.nr} · {v.original_filename}</button>
                  <span className="font-mono">{fmtDate(v.created_at)} · {fmtSize(v.size)}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-[#6B7280] pt-1 border-t border-[#F3F4F6]">
            <span>Hochgeladen</span><span className="text-right font-mono">{fmtDate(doc.created_at)}</span>
            <span>Quelle</span><span className="text-right">{doc.source === "imap" ? `E-Mail${doc.source_meta?.from ? ` · ${doc.source_meta.from}` : ""}` : doc.source === "portal" ? "Mandantenportal" : "Upload"}</span>
            <span>Markierungen</span><span className="text-right">{doc.annotationen || 0}</span>
            {doc.analyzed && <><span className="inline-flex items-center gap-1"><Sparkle size={11} weight="duotone" /> KI</span><span className="text-right truncate">{doc.aussteller || "ausgewertet"}</span></>}
          </div>
        </div>
      )}

      <div className="flex-1 min-h-[360px] flex">
        <AnnotViewer key={`${doc.id}-${doc.version_nr || 1}`} dokumentId={doc.id} akteId={doc.akte_id} dokumentName={doc.original_filename} contentType={doc.content_type} compact onAnnotChange={(list) => onAnnotCount(doc.id, list.length)} />
      </div>
    </div>
  );
}
